import React, { useState, useEffect, createContext, useContext } from 'react';
import { User, LoginCredentials } from '../types';
import apiClient from '../services/api';

interface AuthContextType {
  user: User | null;
  loading: boolean;
  login: (credentials: LoginCredentials) => Promise<void>;
  logout: () => void;
  register: (userData: any) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('authToken');
    console.log('AuthProvider: Checking token:', !!token);
    if (token) {
      console.log('AuthProvider: Token found, loading user...');
      apiClient.getCurrentUser()
        .then(res => {       
          console.log('AuthProvider: User loaded successfully:', res.user);
          setUser(res.user);
        })
        .catch((error) => {
          console.error('AuthProvider: Error loading user:', error);
          console.error('AuthProvider: Error details:', {
            status: error.response?.status,
            data: error.response?.data,
            message: error.message
          });
          localStorage.removeItem('authToken');
          setUser(null);
        })
        .finally(() => {
          console.log('AuthProvider: Loading completed');
          setLoading(false);
        });
    } else {
      console.log('AuthProvider: No token found');
      setLoading(false);
    }
  }, []);

  const login = async (credentials: LoginCredentials) => {
    try {
      const response = await apiClient.login(credentials);
      setUser(response.user);
    } catch (error) {
      throw error;
    }
  };

  const logout = () => {
    apiClient.logout();
    setUser(null);
  };

  const register = async (userData: any) => {
    try {
      await apiClient.register(userData);
    } catch (error) {
      throw error;
    }
  };

  const value: AuthContextType = {
    user,
    loading,
    login,
    logout,
    register,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}; 